// Chamadas de Áreas e CNAE do contrato (04/10/2026). Todas pelo POST único de
// /comercial/propostas-geradas, com `action`.
//
// 🚨 Servidor ANTIGO (os minutos entre publicar a tela e a Lambda): ação que
// ele não conhece cai no "gerar proposta" e volta 400 "proposta_id
// obrigatório". Nenhum dos pedidos daqui leva `proposta_id`, então nada é
// criado por engano — e a resposta é conferida pelo FORMATO, para um 200 de
// outro formato virar falha à vista, nunca "lista vazia".
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from './utils';
import type { DetalheAreas, ItemCarga, Rateio, RespostaPendencias, ResumoCarga } from './areasContrato';

async function chamar(action: string, corpo: Record<string, unknown>) {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, { action, ...corpo },
    { headers: { Authorization: token } });
  return r.data?.body ? JSON.parse(r.data.body) : r.data;
}

export const mensagemDeErro = (e: any) => {
  const m = e?.response?.data?.error || e?.message || 'erro desconhecido';
  // O servidor antigo responde isto a uma ação que ainda não conhece.
  return /proposta_id obrigat/i.test(String(m))
    ? 'O sistema está sendo atualizado. Tente de novo em alguns minutos.'
    : String(m);
};

function exigir<T>(d: any, ok: boolean, oQue: string): T {
  if (!ok) throw new Error(`Resposta inesperada do servidor (${oQue}). Recarregue a página e tente de novo.`);
  return d as T;
}

export async function carregarAreasDoContrato(contratoId: string): Promise<DetalheAreas> {
  const d = await chamar('op_areas_contrato', { contrato_id: contratoId });
  return exigir<DetalheAreas>(d, !!d && !!d.contrato && Array.isArray(d.por_area) && !!d.numeros, 'áreas do contrato');
}

export async function carregarPendenciasDeAreas(): Promise<RespostaPendencias> {
  const d = await chamar('op_areas_pendencias', {});
  return exigir<RespostaPendencias>(d, !!d && Array.isArray(d.linhas) && !!d.contadores && !!d.carga, 'pendências de áreas');
}

export type PedidoDefinir = {
  contrato_id: string; rateio?: Rateio; cnae?: string;
  aplicar_parcelas?: 'sem_setor' | 'abertas' | 'todas' | 'nenhuma'; aplicar_cnae_parcelas?: boolean;
  /** "Aplicar sugestão": só preenche o que continua VAZIO no servidor. A lista
   *  pode estar velha — se alguém definiu a divisão/CNAE no meio tempo, a
   *  escolha dessa pessoa fica (e é ela que vai às parcelas). */
  so_se_vazio?: boolean;
};
export type RespostaDefinir = DetalheAreas & { aplicado: { setor: number; setor_valor: number; cnae: number }; sem_mudanca?: boolean };

export async function definirAreasDoContrato(pedido: PedidoDefinir): Promise<RespostaDefinir> {
  const d = await chamar('op_definir_areas_contrato', pedido);
  return exigir<RespostaDefinir>(d, !!d && !!d.contrato && !!d.aplicado, 'definir áreas');
}

export type RespostaCarga = {
  dry_run: boolean; resumo: ResumoCarga; plano?: ItemCarga[]; aplicado?: ResumoCarga;
  /** Carimbo do plano do ensaio: a aplicação só grava se o plano ainda for este. */
  assinatura?: string;
};

// Ensaio: devolve o que SERIA feito, sem gravar.
export async function ensaiarCarga(): Promise<RespostaCarga> {
  const d = await chamar('op_areas_carga', {});
  return exigir<RespostaCarga>(d, !!d && d.dry_run === true && Array.isArray(d.plano) && !!d.resumo && !!d.assinatura, 'ensaio da carga');
}
// Aplicação: só grava com o booleano `false` explícito E com o carimbo do ensaio
// que a pessoa viu — se os dados mudaram desde então, o servidor recusa (409)
// em vez de gravar um plano que ninguém conferiu.
export async function aplicarCarga(assinatura: string): Promise<RespostaCarga> {
  const d = await chamar('op_areas_carga', { dry_run: false, assinatura });
  return exigir<RespostaCarga>(d, !!d && d.dry_run === false && !!d.aplicado, 'aplicação da carga');
}

// "2 parcelas receberam setor · 1 recebeu CNAE" — para a mensagem de sucesso.
export function resumoAplicado(a: { setor: number; cnae: number }) {
  const partes: string[] = [];
  if (a.setor) partes.push(`${a.setor} parcela${a.setor > 1 ? 's receberam' : ' recebeu'} setor`);
  if (a.cnae) partes.push(`${a.cnae} parcela${a.cnae > 1 ? 's' : ''} em aberto recebe${a.cnae > 1 ? 'ram' : 'u'} CNAE`);
  return partes.join(' · ');
}
